import { useEffect, useMemo, useState } from "react";
import { CalendarRange, AlertTriangle, RotateCcw, Wrench, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { StatCard } from "../../components/shared/StatCard";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";

const DAY_LABELS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const REASONS = [
  { id: "manuelle", label: "Indisponibilité manuelle" },
  { id: "nettoyage", label: "Nettoyage" },
  { id: "reparation", label: "Réparation" },
  { id: "casse", label: "Casse / détérioration" },
  { id: "manquant", label: "Matériel manquant" },
];

function mondayOf(d) { const x = new Date(d); const off = (x.getDay() + 6) % 7; x.setDate(x.getDate() - off); return x; }
const iso = (d) => d.toISOString().slice(0, 10);

export default function PlanningPage() {
  const [start, setStart] = useState(() => mondayOf(new Date()));
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return iso(d); }), [start]);
  const [data, setData] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", qty: 1, reason: "manuelle", start_date: iso(new Date()), end_date: "", notes: "" });

  const load = () => {
    api.get("/admin/logistics/planning", { params: { start_date: days[0], end_date: days[6] } })
      .then((r) => setData(r.data)).catch(() => toast.error("Planning indisponible"));
    api.get("/admin/logistics/blocks").then((r) => setBlocks(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [start]);

  const submitBlock = async () => {
    try {
      await api.post("/admin/logistics/blocks", { ...form, qty: Number(form.qty), end_date: form.end_date || null });
      toast.success("Indisponibilité déclarée — impact immédiat sur la disponibilité réelle");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resolve = async (id) => {
    try { await api.post(`/admin/logistics/blocks/${id}/resolve`); toast.success("Matériel remis en disponibilité après validation"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="planning-loading">Chargement…</div>;

  const engagedRows = data.rows.filter((r) => r.days.some((d) => d.engaged > 0)).length;
  const lateBlocks = blocks.filter((b) => b.reason === "retour_non_confirme").length;

  return (
    <div data-testid="admin-planning-page">
      <p className="overline-ae">B07 — Planning matériel</p>
      <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Planning matériel &amp; disponibilités réelles</h1>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={CalendarRange} label="Articles engagés" value={engagedRows} hint="Sur cette semaine" />
        <StatCard icon={RotateCcw} label="Blocages actifs" value={blocks.length} />
        <StatCard icon={Wrench} label="Nettoyage / réparation" value={blocks.filter((b) => ["nettoyage", "reparation"].includes(b.reason)).length} />
        <StatCard icon={AlertTriangle} label="Retours non confirmés" value={lateBlocks} tone={lateBlocks ? "alert" : "default"} hint="Bloqués jusqu'au contrôle" />
      </div>

      <div className="card-ae mt-6">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => { const d = new Date(start); d.setDate(d.getDate() - 7); setStart(d); }} className="rounded-full border border-border px-3.5 py-1.5 text-xs font-bold text-deepgreen hover:bg-sage-light" data-testid="planning-prev-week">← Semaine précédente</button>
            <button type="button" onClick={() => { const d = new Date(start); d.setDate(d.getDate() + 7); setStart(d); }} className="rounded-full border border-border px-3.5 py-1.5 text-xs font-bold text-deepgreen hover:bg-sage-light" data-testid="planning-next-week">Semaine suivante →</button>
          </div>
          <button type="button" onClick={() => setOpen(true)} className="btn-secondary-ae !px-4 !py-2 !text-[11px]" data-testid="planning-declare-unavailable">
            <Plus className="h-4 w-4" /> Déclarer une indisponibilité
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm" data-testid="planning-grid">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3">Article</th>
                {days.map((d) => {
                  const dt = new Date(d + "T12:00:00");
                  return <th key={d} className="px-2 py-3 text-center">{DAY_LABELS[dt.getDay()]} {dt.getDate()}</th>;
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.rows.map((p) => (
                <tr key={p.product_id} data-testid={`planning-row-${p.slug}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {p.image && <img src={p.image} alt="" className="h-9 w-9 rounded-lg object-cover" />}
                      <div>
                        <p className="font-semibold text-deepgreen">{p.name}</p>
                        <p className="text-xs text-muted-foreground">{p.owned} unités</p>
                      </div>
                    </div>
                  </td>
                  {p.days.map((d) => (
                    <td key={d.date} className="px-1.5 py-3">
                      <div
                        className={`flex h-8 items-center justify-center rounded-md text-[10px] font-bold ${d.blocked > 0 ? "bg-gold/40 text-deepgreen" : d.engaged > 0 ? "bg-sage/40 text-deepgreen" : "bg-cream text-muted-foreground"}`}
                        title={`${d.date} — engagé ${d.engaged}, bloqué ${d.blocked}, disponible ${d.remaining}`}
                        data-testid={`planning-cell-${p.slug}-${d.date}`}
                      >
                        {d.blocked > 0 ? "⛔" : d.engaged > 0 ? d.remaining : ""}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-4 border-t border-border px-5 py-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-sage/40" /> Réservé (chiffre = quantité restante)</span>
          <span className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-gold/40" /> Indisponible (blocage)</span>
          <span className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-cream border border-border" /> Libre</span>
          <span className="ml-auto">Jamais de remise en disponibilité automatique à la date théorique — contrôle Art Event requis.</span>
        </div>
      </div>

      <div className="card-ae mt-6" data-testid="planning-blocks">
        <div className="border-b border-border px-5 py-4">
          <h2 className="font-heading text-lg text-deepgreen">Blocages actifs</h2>
        </div>
        {blocks.length === 0 ? (
          <p className="px-5 py-6 text-sm text-muted-foreground">Aucun blocage actif.</p>
        ) : (
          <ul className="divide-y divide-border">
            {blocks.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center gap-4 px-5 py-3.5" data-testid={`block-${b.id}`}>
                <span className="rounded-full bg-gold-light px-3 py-1 text-xs font-bold text-deepgreen">{REASONS.find((r) => r.id === b.reason)?.label || b.reason}</span>
                <p className="flex-1 text-sm"><span className="font-semibold text-deepgreen">{b.product_name}</span> × {b.qty} — du {b.start_date} au {b.end_date}{b.notes ? ` — ${b.notes}` : ""}</p>
                <button type="button" onClick={() => resolve(b.id)} className="btn-primary-ae !px-4 !py-2 !text-[11px]" data-testid={`block-resolve-${b.id}`}>
                  <CheckCircle2 className="h-4 w-4" /> Remettre en disponibilité
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream" data-testid="block-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">Déclarer une indisponibilité</DialogTitle>
            <DialogDescription>Bloque temporairement un article ou une quantité — impact immédiat sur la disponibilité réelle.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="label-ae" htmlFor="blk-product">Article</label>
              <select id="blk-product" className="input-ae" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="block-product">
                <option value="">Choisir…</option>
                {data.rows.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
              </select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label-ae" htmlFor="blk-qty">Quantité</label><input id="blk-qty" type="number" min="1" className="input-ae" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="block-qty" /></div>
              <div><label className="label-ae" htmlFor="blk-reason">Motif</label>
                <select id="blk-reason" className="input-ae" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="block-reason">
                  {REASONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              <div><label className="label-ae" htmlFor="blk-start">Début</label><input id="blk-start" type="date" className="input-ae" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="block-start" /></div>
              <div><label className="label-ae" htmlFor="blk-end">Fin (vide = jusqu'à résolution)</label><input id="blk-end" type="date" className="input-ae" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="block-end" /></div>
            </div>
            <div><label className="label-ae" htmlFor="blk-notes">Notes</label><input id="blk-notes" className="input-ae" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="block-notes" /></div>
            <button type="button" onClick={submitBlock} className="btn-primary-ae modal-sticky-btn w-full" data-testid="block-submit">Déclarer le blocage</button>
            <p className="text-xs text-muted-foreground">Le blocage impacte immédiatement le calcul de disponibilité. La remise en disponibilité reste une action manuelle validée.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
